import { generateMessageId } from '../../utils/messageUtils';

/**
 * Shared message builders — single source of truth for message object shapes.
 * Eliminates field drift across AnonExchangeFlow, ConnectionPageShared,
 * and SharingSelectionOverlay.
 */

/**
 * Build a received chat message from a WebSocket payload.
 * Used by: AnonExchangeFlow, ConnectionPageShared
 */
export function buildReceivedChatMessage(payload, fallbackId) {
  const message = {
    id: payload.id || fallbackId || generateMessageId(),
    type: 'chat',
    sender: payload.sender || 'unknown',
    senderId: payload.senderId || null,
    content: payload.content,
    contentType: payload.contentType || null,
    isInviteCta: payload.isInviteCta || false,
    isFreeform: payload.isFreeform || false,
    timestamp: payload.timestamp || new Date().toISOString(),
  };
  if (payload.anonName) {
    message.anonName = payload.anonName;
  }
  if (payload.canCreateProfile !== undefined) {
    message.canCreateProfile = payload.canCreateProfile;
  }
  return message;
}

/**
 * Build a sent chat message for both WebSocket send and local state.
 * `classified` marks the Anon's classified share bubble (social-track #9):
 * the verbatim words with the classification riding along so the receiving
 * side renders them linked — and NOT freeform (share events stay out of the
 * dashboard's real-chat counts).
 * Used by: ConnectionPageShared, ProfileConnectionPage (greeting),
 * AnonConnectionPage (contact stage)
 */
export function buildSentChatMessage({ messageId, senderType, senderId, content, classified }) {
  const msg = {
    id: messageId || generateMessageId(),
    type: 'chat',
    sender: senderType,
    senderId: senderId,
    content: content,
    timestamp: new Date().toISOString(),
    isFreeform: true,
    isInviteCta: false,
  };
  if (classified) {
    msg.isFreeform = false;
    msg.shareType = classified.type;
    msg.shareValue = classified.value;
  }
  return msg;
}

/**
 * Build the Anon's `contact` DATA message (skipDisplay) — name and/or contact
 * record riding to the Profile. This message type must keep flowing:
 * conversion identity-healing reads self-declared phones from `contact`
 * messages (migration 021). One authoritative write per state change.
 * Used by: AnonConnectionPage (commitAnonContact, sendNameData)
 */
export function buildAnonContactDataMessage({ senderId, anonData, contactType }) {
  const msg = {
    id: generateMessageId(),
    type: 'contact',
    sender: 'anon',
    senderId,
    timestamp: new Date().toISOString(),
    skipDisplay: true,
    anonData,
  };
  if (contactType) msg.contactType = contactType;
  return msg;
}

/**
 * Build an image chat message for photo sharing.
 * Used by: ProfileConnectionPage, AnonConnectionPage (optimistic display + send)
 */
export function buildImageChatMessage({ senderId, senderType, imageUrl, uploading = false }) {
  return {
    id: generateMessageId(),
    type: 'chat',
    contentType: 'image',
    sender: senderType,
    senderId: senderId,
    content: imageUrl,
    uploading,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Build a `moment` message — a photo (or, in the later increment, a short line)
 * left on a meeting, keyed to the shared per-night key. A hidden chat_messages
 * type (NON_DISPLAY_TYPES): delivered + recovered on the DB-first rail, never
 * shown in chat. Each moment is its OWN entry with a unique id (first-write-wins).
 * The photo rides under `photoUrl` — NEVER contentType:'image', which would leak
 * it into PhotosSection, chat previews and the analytics dashboard.
 * Used by: services/moments/momentStore.js (sendMoment)
 */
export function buildMomentMessage({
  momentType,
  nightKey,
  meetingKey,
  photoUrl,
  text,
  sender,
  senderId,
  senderProfileUserId,
}) {
  const msg = {
    id: generateMessageId(),
    type: 'moment',
    momentType, // 'photo' | 'text'
    nightKey,
    sender, // 'profile' | 'anon' — the author
    senderId,
    timestamp: new Date().toISOString(),
  };
  if (meetingKey) msg.meetingKey = meetingKey; // the meeting it belongs to (by key with F)
  if (photoUrl) msg.photoUrl = photoUrl;
  if (text) msg.text = text;
  if (senderProfileUserId) msg.senderProfileUserId = senderProfileUserId;
  return msg;
}

/**
 * Build a system meeting-banner message — the single shape definition.
 * Content is the venue line(s) only; the date renders at display time from
 * meetingDate, pinned to meeting-local time by meetingUtcOffset (minutes east
 * of UTC; absent → viewer-local). timestamp doubles as the sort key.
 * Used by: qrDetection, qrPXPExchange (host + guest), ProfileConnectionPage
 * (recreation), AnonConnectionPage (local display only)
 */
export function buildSystemMeetingMessage({ id, content, meetingDate, meetingUtcOffset }) {
  return {
    id,
    type: 'system',
    content,
    meetingDate,
    meetingUtcOffset,
    sender: 'system',
    senderId: null,
    timestamp: meetingDate,
  };
}

/**
 * Build a single-channel share line — the Profile's share as speech
 * ("My insta is brilarsen", value linked at render). Social-track #13:
 * every chat share posts a new line; unique id per share action.
 * Used by: ProfileConnectionPage (share sheet one-taps, composer tray)
 */
export function buildShareLineMessage({ senderId, shareType, shareValue }) {
  return {
    id: generateMessageId(),
    type: 'contact_share',
    speech: true,
    shareType,
    shareValue,
    sender: 'profile',
    senderId,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Build the multi-select share line — "Here is my contact", the word linking
 * to the peer-side detail page (social-track #4; replaces the card bubble).
 * Used by: ProfileConnectionPage (SharingSelectionOverlay completion)
 */
export function buildContactLinkMessage({ senderId }) {
  return {
    id: generateMessageId(),
    type: 'contact_share',
    speech: true,
    contactLink: true,
    sender: 'profile',
    senderId,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Build a contact_share message — card-style (Profile) or inline (Anon).
 * Both are LEGACY shapes now (kept so historic rooms round-trip; new exchanges
 * send share lines via the builders above).
 * Used by: AnonExchangeFlow, ConnectionPageShared, SharingSelectionOverlay
 */
export function buildContactShareMessage(payload) {
  const base = {
    id: payload.id || generateMessageId(),
    type: 'contact_share',
    sender: payload.sender || 'profile',
    senderId: payload.senderId || null,
    timestamp: payload.timestamp || new Date().toISOString(),
  };

  // Share lines (shares-as-speech) pass through shape-complete — a speech
  // message has no contacts[] and must never read as an "empty" inline share
  // (device pass 2: AnonExchangeFlow's empty-guard was eating them)
  if (payload.speech) {
    const speechMsg = { ...base, speech: true };
    if (payload.contactLink) speechMsg.contactLink = true;
    if (payload.shareType) speechMsg.shareType = payload.shareType;
    if (payload.shareValue) speechMsg.shareValue = payload.shareValue;
    return speechMsg;
  }

  if (payload.cardStyle) {
    return {
      ...base,
      cardStyle: true,
      profileName: payload.profileName,
      sharedContactTypes: payload.sharedContactTypes || [],
      profileData: payload.profileData || null,
    };
  }

  return {
    ...base,
    contacts: payload.contacts || [],
  };
}

/**
 * Build Mark's invite — the isInviteCta chat message (signup-invite-simple).
 * Only an admin account's write lands: the server refuses anyone else's
 * (migration 041), so canCreateProfile is always true on a landed invite; it
 * still rides for older bundles that route on it.
 * Used by: services/chat/royaleInvite.js
 */
export function buildInviteCtaMessage({ senderId }) {
  return {
    id: generateMessageId(),
    type: 'chat',
    sender: 'profile',
    senderId,
    content: 'Invite to Royale',
    isInviteCta: true,
    canCreateProfile: true,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Build a `metadata_updated` message — the host passing its record (a late place, a
 * venue confirm or change, a re-meet's new meeting) to the other person. The single
 * shape: every sender carries its profile id, so all of a host's updates in a room
 * land on one stored row (`meta_<room>_<profile>`, chatService) and the latest send
 * wins on recovery. Coordinates never travel — the serializer strips them (messageStore).
 * Used by: qrDetection, returningGuest, venueConfirmation, ProfileConnectionPage,
 * ProfileConnectionDetailPage
 */
export function buildMetadataUpdatedMessage({ connectionMetadata, senderProfileUserId, meeting }) {
  return {
    id: generateMessageId(),
    type: 'metadata_updated',
    sender: 'profile',
    senderId: senderProfileUserId,
    senderProfileUserId,
    connectionMetadata,
    // The host's verdict on a repeat connect, as its own field (signup E3b-1
    // step 10): { kind, key } — the other phone pins a reconnect from it, never
    // decides alone. Absent on every other update (an old app ignores it).
    ...(meeting ? { meeting } : {}),
    timestamp: new Date().toISOString(),
  };
}
